// src/utils/tiptapEditorConfig.js

/**
 * Configuração do editor rich text (Tiptap) do Litterae.
 *
 * ⚠️ ESTE MÓDULO É UM CONSUMIDOR DE CONTRATO. O contrato está em
 * `docs/refatoracao-conteudo-html.md`: a whitelist do `HtmlNormalizer` e o
 * vocabulário fechado de classes. A regra que governa tudo aqui:
 *
 *     só pode existir formatação que sobreviva ao normalizador.
 *
 * Formatação que a whitelist descarta é perda silenciosa: o editor guarda o estado
 * em memória, o usuário vê o resultado na tela, salva — e só descobre no
 * recarregamento que sumiu. Nenhum erro é emitido em lugar nenhum.
 *
 * Por isso duas coisas neste arquivo parecem estranhas e são deliberadas:
 *
 *  1. `code` é DESLIGADO do StarterKit. Ele emite `<code>`, que a whitelist
 *     desembrulha.
 *  2. alinhamento, família e corpo da fonte têm `renderHTML` sobrescrito para
 *     emitirem CLASSE em vez de `style`. A Regra 2 do normalizador mata `style`;
 *     sem a sobrescrita, o usuário formataria e perderia.
 */

import { StarterKit } from "@tiptap/starter-kit";
import { FontFamily, FontSize, TextStyle } from "@tiptap/extension-text-style";
import { TextAlign } from "@tiptap/extension-text-align";
import { Superscript } from "@tiptap/extension-superscript";
import { Subscript } from "@tiptap/extension-subscript";
import { Link } from "@tiptap/extension-link";
import { Image } from "@tiptap/extension-image";
import { Placeholder } from "@tiptap/extension-placeholder";
import { Extension, Node } from "@tiptap/core";
import { Plugin } from "@tiptap/pm/state";
import {
  Table,
  TableCell,
  TableHeader,
  TableRow,
} from "@tiptap/extension-table";

/**
 * O vocabulário fechado de classes, espelhando `HtmlVocabulary.ClassGroups` do
 * backend e o CSS escopado em `.text-body--html` (`TextDisplayer.vue`).
 *
 * Os três precisam concordar. Se divergirem, a formatação existe num lado e some
 * no outro — e some sem erro.
 */
export const ALIGN_CLASSES = {
  center: "lit-align-center",
  right: "lit-align-right",
  justify: "lit-align-justify",
  // "left" é o padrão e NÃO tem classe, de propósito: a whitelist não a conhece.
};

export const FONT_FAMILY_CLASSES = {
  serif: "lit-font-serif",
  sans: "lit-font-sans",
  mono: "lit-font-mono",
};

export const FONT_SIZE_CLASSES = {
  sm: "lit-size-sm",
  lg: "lit-size-lg",
};

/**
 * Recuo de primeira linha. Uma classe só, e não uma tabela como as de cima: aqui
 * não há valores concorrentes, só presença ou ausência — sem recuo é o padrão e,
 * como o alinhamento à esquerda, não tem classe.
 *
 * A classe guarda a INTENÇÃO, não a medida: o tamanho do recuo vive no CSS de
 * `.text-body--html`. É a regra do vocabulário inteiro.
 */
export const INDENT_CLASS = "lit-indent";

/**
 * Filete que abre o rodapé de notas na importação de PDF — igual ao grupo do
 * backend, onde a classe só é aceita em `<hr>`.
 */
export const NOTE_RULE_CLASS = "lit-note-rule";

/**
 * Título corrente do impresso na importação de PDF — igual ao grupo do backend,
 * onde a classe só é aceita em `<p>`.
 */
export const RUNNING_HEAD_CLASS = "lit-running-head";

/** Blocos onde o recuo é aceito — igual ao grupo do backend. */
export const INDENTABLE_TYPES = ["paragraph"];

/** Blocos onde o alinhamento é aceito — igual ao grupo do backend. */
export const ALIGNABLE_TYPES = [
  "paragraph",
  "heading",
  "blockquote",
  "listItem",
];

/** Devolve a chave do vocabulário a partir da classe presente no elemento. */
function keyFromClass(element, table) {
  for (const [key, className] of Object.entries(table)) {
    if (element.classList?.contains(className)) return key;
  }
  return null;
}

/**
 * Alinhamento emitindo classe, não `style`.
 *
 * O `left` devolve `{}` de propósito: alinhamento à esquerda é o padrão e não tem
 * classe no vocabulário, então não há nada a marcar.
 */
export const LitteraeTextAlign = TextAlign.extend({
  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          textAlign: {
            default: this.options.defaultAlignment,
            parseHTML: (element) =>
              keyFromClass(element, ALIGN_CLASSES) ??
              this.options.defaultAlignment,
            renderHTML: (attributes) => {
              const className = ALIGN_CLASSES[attributes.textAlign];
              return className ? { class: className } : {};
            },
          },
        },
      },
    ];
  },
});

/**
 * Família da fonte emitindo classe. O valor do atributo passa a ser a CHAVE do
 * vocabulário (`serif` | `sans` | `mono`), não um nome de fonte arbitrário — é o
 * que impede o editor de oferecer uma fonte que a whitelist não carrega.
 */
export const LitteraeFontFamily = FontFamily.extend({
  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          fontFamily: {
            default: null,
            parseHTML: (element) => keyFromClass(element, FONT_FAMILY_CLASSES),
            renderHTML: (attributes) => {
              const className = FONT_FAMILY_CLASSES[attributes.fontFamily];
              return className ? { class: className } : {};
            },
          },
        },
      },
    ];
  },
});

/** Corpo da fonte emitindo classe. Mesma lógica da família. */
export const LitteraeFontSize = FontSize.extend({
  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          fontSize: {
            default: null,
            parseHTML: (element) => keyFromClass(element, FONT_SIZE_CLASSES),
            renderHTML: (attributes) => {
              const className = FONT_SIZE_CLASSES[attributes.fontSize];
              return className ? { class: className } : {};
            },
          },
        },
      },
    ];
  },
});

/**
 * Recuo de primeira linha emitindo classe.
 *
 * Extensão PRÓPRIA, e não a extensão de alguém do Tiptap estendida: recuo não é
 * conceito que o StarterKit conheça, então não há comportamento a herdar — só o
 * atributo global e o comando que o liga e desliga.
 *
 * O atributo é booleano porque o vocabulário tem uma classe só. Guardar aqui o
 * tamanho do recuo (o EPUB do 1889 usa ora 14px, ora 28px) daria ao editor um
 * estado que a whitelist não carrega, e o usuário perderia a diferença ao salvar
 * sem nenhum aviso — a perda silenciosa que este arquivo inteiro existe para
 * evitar.
 */
/**
 * Filete do rodapé de notas, preservando a classe no `<hr>`.
 *
 * Existe pelo mesmo motivo do recuo acima: o vocabulário aceita `lit-note-rule`
 * em `<hr>`, mas o HorizontalRule do StarterKit não guarda atributo nenhum — sem
 * esta extensão, abrir e salvar um texto importado de PDF apagaria a classe, e o
 * filete de nota viraria um separador de cena de largura inteira. Perda
 * silenciosa, que é o que este arquivo existe para evitar.
 *
 * Booleano, e não a classe crua: há uma classe só no grupo, e o que importa é a
 * presença dela.
 */
export const LitteraeNoteRule = Extension.create({
  name: "litteraeNoteRule",

  addGlobalAttributes() {
    return [
      {
        types: ["horizontalRule"],
        attributes: {
          noteRule: {
            default: false,
            parseHTML: (element) =>
              element.classList?.contains(NOTE_RULE_CLASS) ?? false,
            renderHTML: (attributes) =>
              attributes.noteRule ? { class: NOTE_RULE_CLASS } : {},
          },
        },
      },
    ];
  },
});

/**
 * Título corrente do impresso, preservando a classe no `<p>`.
 *
 * Mesmo motivo das duas extensões acima: sem ela, abrir e salvar um texto
 * importado de PDF apagaria a classe, e a linha do cabeçalho voltaria a se
 * confundir com o corpo. A whitelist aceita `lit-running-head` em `<p>`; quem a
 * carrega através do editor é isto.
 *
 * Só um atributo booleano, sem comando: não há por que o usuário marcar um
 * parágrafo como título corrente — quem sabe disso é a importação, que mediu a
 * posição dele na página.
 */
export const LitteraeRunningHead = Extension.create({
  name: "litteraeRunningHead",

  addGlobalAttributes() {
    return [
      {
        types: ["paragraph"],
        attributes: {
          runningHead: {
            default: false,
            parseHTML: (element) =>
              element.classList?.contains(RUNNING_HEAD_CLASS) ?? false,
            renderHTML: (attributes) =>
              attributes.runningHead ? { class: RUNNING_HEAD_CLASS } : {},
          },
        },
      },
    ];
  },
});

export const LitteraeIndent = Extension.create({
  name: "litteraeIndent",

  addOptions() {
    return { types: INDENTABLE_TYPES };
  },

  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          indent: {
            default: false,
            parseHTML: (element) =>
              element.classList?.contains(INDENT_CLASS) ?? false,
            renderHTML: (attributes) =>
              attributes.indent ? { class: INDENT_CLASS } : {},
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      /**
       * Liga/desliga o recuo nos blocos da seleção.
       *
       * `updateAttributes` por TIPO, e não um comando por bloco: é como o
       * Tiptap trata atributo global, e cobre a seleção que atravessa vários
       * parágrafos de uma vez.
       */
      toggleIndent:
        () =>
        ({ editor, commands }) => {
          const ativo = this.options.types.some((type) =>
            editor.isActive(type, { indent: true })
          );

          return this.options.types.every((type) =>
            commands.updateAttributes(type, { indent: !ativo })
          );
        },
    };
  },
});

/**
 * Blocos que podem hospedar `id` — âncora de bloco, como o título de capítulo
 * que o sumário do EPUB aponta.
 */
export const ANCHORABLE_TYPES = ["paragraph", "heading"];

/**
 * `id` em bloco.
 *
 * O schema do Tiptap não prevê `id` em lugar nenhum, e por isso a importação
 * perdia todo alvo de link: o normalizador PRESERVA `id` (com prefixo `lit-`) e
 * o leitor o renderiza, mas o conteúdo passa pelo editor antes de ser salvo, e
 * era ali que ele morria.
 *
 * Só preserva o que chega — não há comando para criar `id`, porque não é coisa
 * que se escreva à mão. Quem os produz é a importação (ver `EpubLinkResolver` no
 * backend).
 */
export const LitteraeBlockAnchor = Extension.create({
  name: "litteraeBlockAnchor",

  addOptions() {
    return { types: ANCHORABLE_TYPES };
  },

  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          id: {
            default: null,
            parseHTML: (element) => element.getAttribute("id"),
            // `id` vazio não vira atributo: `id=""` no conteúdo seria ruído, e a
            // whitelist o descartaria de qualquer forma.
            renderHTML: (attributes) =>
              attributes.id ? { id: attributes.id } : {},
          },
        },
      },
    ];
  },
});

/**
 * Âncora: o alvo de um link interno, sem largura.
 *
 * É NÓ, não atributo de mark, e a diferença é a razão de ele existir. Um `id` é
 * uma POSIÇÃO; a mark de link é um INTERVALO, que o ProseMirror fatia quando há
 * formatação por dentro — e cada pedaço reemitiria o mesmo `id`, criando
 * duplicata. Também não serve como atributo do bloco: no 1889 há 45 parágrafos
 * com mais de um alvo (o pior tem quatro chamadas de nota), e bloco tem um `id`
 * só.
 *
 * Nó atômico inline não se divide, porque não tem conteúdo para dividir, e
 * podem existir quantos forem no mesmo parágrafo.
 *
 * Invisível no conteúdo, por construção: emite `<a id="…"></a>`, que não
 * contribui com caractere nenhum na projeção — nenhuma nota já existente sai do
 * lugar por causa dele. A marca que o editor mostra é CSS, e só no editor (ver
 * `.rte-content .ProseMirror a[id]:empty` em main.css).
 */
export const LitteraeAnchor = Node.create({
  name: "anchor",
  group: "inline",
  inline: true,
  atom: true,
  selectable: true,
  draggable: false,

  addAttributes() {
    return {
      id: { default: null },
    };
  },

  /**
   * `<a>` com `id`, sem `href` e sem conteúdo. As três condições juntas: com
   * `href` é link (assunto do `LitteraeLink`), e com conteúdo é link cujo texto
   * precisa sobreviver — o backend já separa os dois papéis na importação, e
   * aqui a regra recusa para o ProseMirror descer nos filhos.
   */
  parseHTML() {
    return [
      {
        tag: "a[id]",
        getAttrs: (element) => {
          if (element.getAttribute("href")) return false;
          if (element.childNodes.length > 0) return false;

          const id = element.getAttribute("id");
          return id ? { id } : false;
        },
      },
    ];
  },

  renderHTML({ node }) {
    return ["a", { id: node.attrs.id }];
  },
});

/**
 * `<span>` só sobrevive carregando classe do vocabulário.
 *
 * O `TextStyle` padrão marca **qualquer** `<span>`, mesmo sem atributo algum —
 * então colar do Word (`<span style="mso-…">`) deixava um `<span>` vazio no
 * conteúdo. O normalizador o desembrulha, e editor e conteúdo gravado divergiam.
 * Medido pela suite de colagem.
 */
export const LitteraeTextStyle = TextStyle.extend({
  parseHTML() {
    return [
      {
        tag: "span",
        getAttrs: (element) =>
          keyFromClass(element, FONT_FAMILY_CLASSES) ||
          keyFromClass(element, FONT_SIZE_CLASSES)
            ? {}
            : false,
      },
    ];
  },
});

/**
 * Link que emite apenas `href`.
 *
 * O padrão do Tiptap acrescenta `target="_blank"` e
 * `rel="noopener noreferrer nofollow"`. A whitelist só aceita `href` e `id` em
 * `<a>`, e o renderizador também filtra — então esses atributos seriam gravados no
 * conteúdo para nunca chegarem à tela.
 *
 * ⚠️ **Não dá para limpar isso com `configure`.** Medido: tanto
 * `StarterKit.configure({ link: { HTMLAttributes: {} } })` quanto
 * `Link.configure({ HTMLAttributes: {} })` continuam emitindo os dois — o merge de
 * `HTMLAttributes` é profundo, e `{}` não remove nada. Sobrescrever `addOptions` é
 * o único caminho que funciona.
 *
 * Abrir link em nova aba é decisão de **leitura**, não de conteúdo: se for
 * desejado, o lugar é o renderizador, na hora de montar o `<a>`.
 */
export const LitteraeLink = Link.extend({
  /**
   * Só `href`. O Link padrão declara `target` e `rel` como ATRIBUTOS, e os lê do
   * HTML colado — então limpar só o `HTMLAttributes` não basta: colar um link de
   * página web reintroduziria `target="_blank"` no conteúdo. Medido pela suite de
   * colagem.
   */
  addAttributes() {
    return {
      href: { default: null },
    };
  },

  addOptions() {
    return {
      ...this.parent?.(),
      HTMLAttributes: {},
      // Espelha as restrições de href do normalizador: sem isto o editor aceitaria
      // um link que o backend descartaria depois.
      protocols: ["http", "https", "mailto"],
      autolink: true,
      // O `openOnClick` do Tiptap faz `window.open(href)`, e isso tira quem edita
      // da página — possivelmente com alteração não salva. Fica desligado; o
      // plugin abaixo trata o caso que NÃO tem esse custo.
      openOnClick: false,
    };
  },

  /**
   * Clique em âncora interna rola até o alvo, dentro do próprio editor.
   *
   * Externo e interno deixaram de ser a mesma coisa quando a importação de EPUB
   * passou a trazer navegação de verdade (838 links internos no 1889, entre
   * sumário e notas de rodapé). Abrir link externo custa a página; saltar para
   * uma âncora não sai do documento nem da edição — só rola. Conferir se o
   * sumário importado aponta certo é exatamente o que se quer fazer aqui.
   *
   * As três guardas existem para não atrapalhar a EDIÇÃO do texto do link:
   * arrastar para selecionar deixa a seleção aberta, clique duplo seleciona a
   * palavra, e clique com modificador é gesto de quem quer outra coisa. Só o
   * clique simples e limpo salta.
   */
  addProseMirrorPlugins() {
    return [
      // Os plugins do pai vêm junto: são eles que fazem autolink e colagem de
      // link. Substituí-los em vez de somar quebraria os dois em silêncio.
      ...(this.parent?.() ?? []),

      new Plugin({
        props: {
          handleDOMEvents: {
            click: (view, event) => {
              if (event.button !== 0 || event.detail !== 1) return false;
              if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) {
                return false;
              }

              const anchor = event.target?.closest?.('a[href^="#"]');
              if (!anchor || !view.dom.contains(anchor)) return false;

              const target = findAnchorTarget(view.dom, anchor.getAttribute("href"));
              if (!target) return false;

              // Sem `preventDefault` o cursor iria para dentro do link e a rolagem
              // o deixaria fora da tela — dá para digitar num ponto que não se vê.
              event.preventDefault();
              target.scrollIntoView({ block: "center", behavior: "smooth" });
              return true;
            },
          },
        },
      }),
    ];
  },
});

/**
 * Elemento apontado por um `href` de âncora, procurado DENTRO do editor.
 *
 * Escopado na raiz do editor de propósito: `document.getElementById` acharia
 * também `id` do DOM da aplicação. É a mesma preocupação que faz o normalizador
 * prefixar todo `id` de conteúdo com `lit-`.
 */
export function findAnchorTarget(root, href) {
  const id = String(href ?? "").slice(1);
  if (!id || !root) return null;

  // `CSS.escape` porque `id` é conteúdo do usuário e pode trazer ponto, dois
  // pontos ou acento — tudo com significado dentro de um seletor.
  const escaped =
    typeof CSS !== "undefined" && CSS.escape ? CSS.escape(id) : id;

  try {
    return root.querySelector(`#${escaped}`);
  } catch {
    return null;
  }
}

/**
 * Espelho de `HtmlNormalizer.TryNormalizeImage` + `AllowedImageSchemes`.
 *
 * A limpeza de espaço em branco DENTRO da URL não é preciosismo: é ela que
 * impede `"java&#9;script:"` de atravessar a checagem de esquema, e o backend faz
 * o mesmo em `StripUrlWhitespace`. Os dois lados precisam recusar as mesmas URLs,
 * senão o editor mostra uma imagem que o backend vai descartar — e o usuário só
 * descobre no recarregamento.
 */
export function isAllowedImageSrc(value) {
  const url = String(value ?? "")
    .replace(/[\t\n\r]/g, "")
    .trim();

  const colon = url.indexOf(":");
  if (colon <= 0) return false; // relativo ou sem esquema: não há de onde carregar

  // Whitelist, não blacklist — igual ao backend.
  return ["http", "https"].includes(url.slice(0, colon).toLowerCase());
}

/**
 * Imagem que emite apenas `src` e `alt`.
 *
 * A `Image` padrão declara cinco atributos — `src`, `alt`, `title`, `width` e
 * `height`. Os três últimos a whitelist derruba, e derrubar atributo é a perda
 * silenciosa que o cabeçalho deste módulo existe para prevenir: o usuário
 * redimensionaria a imagem na tela, salvaria, e ela voltaria no tamanho original
 * sem que nada tivesse falhado. Declarando só os dois que sobrevivem, o editor
 * nunca chega a oferecer o que não pode guardar.
 *
 * O dimensionamento fica com o CSS compartilhado (`.text-body--html img`, em
 * `assets/main.css`), que serve leitor e editor de uma vez.
 *
 * ⚠️ A imagem é **hotlink**: o `src` aponta para o servidor de origem, e o
 * conteúdo guarda só o endereço. É a única rota possível enquanto não houver
 * storage de mídia — `texts.filepath` é um arquivo por texto, sem onde pôr
 * binário. A consequência é que imagem some se o site de origem sair do ar.
 */
export const LitteraeImage = Image.extend({
  addAttributes() {
    return {
      src: { default: null },
      alt: { default: null },
    };
  },

  /**
   * Recusa no PARSING o que o normalizador recusaria depois.
   *
   * A regra padrão aceita qualquer `img[src]` (só `data:` fica de fora quando
   * `allowBase64` é falso). Medido: sem esta sobrescrita, colar de um EPUB
   * (`src="../images/x.jpg"`) ou uma `src="javascript:…"` deixava a `<img>` viva
   * no editor para o backend descartar em seguida — o usuário via a imagem,
   * salvava, e ela não voltava.
   *
   * `getAttrs` devolvendo `false` rejeita a regra; a `<img>` some e o texto em
   * volta segue intacto. Mesmo mecanismo do `LitteraeTextStyle` acima.
   */
  parseHTML() {
    return [
      {
        tag: "img[src]",
        getAttrs: (element) =>
          isAllowedImageSrc(element.getAttribute("src")) ? null : false,
      },
    ];
  },

  addOptions() {
    return {
      ...this.parent?.(),
      HTMLAttributes: {},
      // Inline, e não bloco: numa área copiada de página web a imagem vem no meio
      // do parágrafo, e `inline: false` a arrancaria dali para virar irmã do <p>.
      // Espelha `img` estar em `InlineTags` no vocabulário do backend.
      inline: true,
      // `data:` não passa no normalizador (AllowedImageSchemes é http/https), então
      // o editor também não pode aceitar — senão a imagem colada do Word apareceria
      // na tela e sumiria no recarregamento.
      allowBase64: false,
      // Redimensionar gravaria width/height, que a whitelist não aceita.
      resize: false,
    };
  },
});

/**
 * Imagem COM legenda: `<figure><img><figcaption>`.
 *
 * ⚠️ **Por que é um nó separado, e não um atributo do `LitteraeImage`.**
 * `<figure>` é bloco, e bloco não pode viver dentro de `<p>` — o parser quebraria
 * o parágrafo em volta e o normalizador emitiria árvore diferente da que o editor
 * mostra. Logo: imagem sem legenda continua INLINE no parágrafo (o caso da
 * colagem de página web, que foi o que originou a imagem aqui); imagem com
 * legenda vira um bloco próprio. Pôr legenda numa imagem a tira do parágrafo, e
 * isso é a estrutura do documento mudando de verdade, não um efeito colateral.
 *
 * **A legenda é ATRIBUTO, não conteúdo editável.** O nó é `atom`: quem edita a
 * legenda é o campo do painel da barra, não o cursor dentro da figura. Isso
 * mantém o nó simples e a legenda como texto puro — o negrito e a centralização
 * vêm do CSS de `.text-body--html figcaption`, não de marcação gravada. Emitir
 * `<strong>` aqui gravaria formatação que é decisão de LEITURA.
 *
 * **A legenda conta na projeção.** `figcaption` está em `ProjectionBreakTags`, e
 * o texto dela é texto que o leitor lê: entra na cota, e pode receber nota. É a
 * escolha coerente — a alternativa (texto na tela fora da projeção) quebraria a
 * caminhada de offsets do renderizador.
 */
export const LitteraeFigure = Node.create({
  name: "figure",
  group: "block",
  // `atom`: sem conteúdo editável dentro. A legenda entra pelo painel.
  atom: true,
  draggable: true,

  addAttributes() {
    return {
      src: { default: null },
      alt: { default: null },
      caption: { default: "" },
    };
  },

  /**
   * Aceita `<figure>` colado de página web, o que preserva legenda de verdade —
   * é assim que Wikipédia e portais marcam imagem legendada.
   *
   * Com `<img>` de `src` inaproveitável a regra é RECUSADA (`false`), e aí o
   * ProseMirror desce nos filhos: se houver um `<img>` válido lá dentro, ele
   * ainda entra como imagem inline. Recusar não é o mesmo que descartar.
   *
   * ⚠️ Figura SEM `<img>` nenhum é caso à parte: é a MOLDURA, e ela é aceita.
   * Quem a produz é a importação de EPUB (ver `EpubImagePlaceholder` no
   * backend), onde a imagem mora dentro do pacote e não sobrevive à whitelist —
   * o que fica é o lugar dela, com a legenda. Sem esta regra o editor
   * desmontaria a moldura na importação, muito antes de qualquer salvar: a
   * `<figure>` viraria nada e a legenda cairia como parágrafo solto no meio do
   * texto.
   */
  parseHTML() {
    return [
      {
        tag: "figure",
        getAttrs: (element) => {
          const caption =
            element.querySelector("figcaption")?.textContent?.trim() ?? "";

          // A distinção é entre "não tem imagem" e "tem imagem que não serve".
          // Só a primeira é moldura; a segunda continua recusada, para o `<img>`
          // de dentro ter a chance de entrar sozinho.
          const image = element.querySelector("img");
          if (!image) return { src: null, alt: null, caption };

          const src = image.getAttribute("src");
          if (!src || !isAllowedImageSrc(src)) return false;

          return { src, alt: image.getAttribute("alt"), caption };
        },
      },
    ];
  },

  renderHTML({ node }) {
    const { src, alt, caption } = node.attrs;
    const legenda = String(caption ?? "").trim();

    const partes = [];

    // Sem `src` a figura é a moldura: não emite `<img>` nenhum. O desenho dela
    // é CSS (`figure:not(:has(img))` em main.css), que vale igual no editor e no
    // leitor — o rótulo "Imagem" é decisão de leitura, e por isso não entra no
    // conteúdo nem na projeção.
    if (src) partes.push(["img", alt ? { src, alt } : { src }]);

    // Sem legenda a `<figcaption>` não é emitida vazia: `<figcaption></figcaption>`
    // seria ruído gravado no conteúdo, e um bloco vazio na projeção.
    if (legenda) partes.push(["figcaption", {}, legenda]);

    return ["figure", {}, ...partes];
  },

  addCommands() {
    return {
      setFigure:
        (attributes) =>
        ({ commands }) =>
          commands.insertContent({ type: this.name, attrs: attributes }),
    };
  },
});

/**
 * Escapa texto para inserção como HTML.
 */
function escapeHtml(text) {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

/**
 * Converte texto puro em HTML de parágrafos, **com as mesmas regras de bloco que o
 * `PlaintextToHtml` do backend**: linha em branco separa `<p>`, quebra simples vira
 * `<br>`.
 *
 * A coerência importa: um texto colado aqui e o mesmo texto convertido pelo botão
 * do Sys devem produzir a mesma estrutura. Se divergirem, o usuário vê resultados
 * diferentes para o mesmo conteúdo, dependendo do caminho por onde entrou.
 */
export function plaintextToEditorHtml(text) {
  const normalized = String(text ?? "").replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  if (!normalized.trim()) return "";

  return normalized
    .split(/\n{2,}/)
    .map((block) => block.split("\n").map(escapeHtml).join("<br>"))
    .filter((block) => block.trim().length > 0)
    .map((block) => `<p>${block}</p>`)
    .join("");
}

/**
 * Colagem de texto puro com quebra dura — o caso do PDF.
 *
 * O `CustomTextarea` já fazia isso via `:unwrapPastedLineBreaks="true"`, e o
 * `AddTextForm` usa. Perder esse comportamento na troca de componente seria
 * regressão silenciosa: o texto colado de PDF viria quebrado no meio das frases.
 *
 * Só age quando **não** há `text/html` na área de transferência. Colagem com
 * marcação segue o caminho normal, onde o schema do editor filtra o que não
 * conhece — que é a defesa contra o lixo do Word.
 */
export const PdfPasteUnwrap = Extension.create({
  name: "pdfPasteUnwrap",

  addOptions() {
    return {
      enabled: false,
      /**
       * A transformação a aplicar ao texto colado. Recebida como opção, não
       * importada aqui: a extensão decide **quando** transformar, não **como**.
       * Quem sabe qual heurística usar é o componente.
       */
      transform: (text) => text,
    };
  },

  addProseMirrorPlugins() {
    const extension = this;

    return [
      new Plugin({
        props: {
          handlePaste: (view, event) => {
            if (!extension.options.enabled) return false;

            const clipboard = event.clipboardData;
            if (!clipboard) return false;

            // Colagem com marcação: deixa o schema fazer a filtragem.
            if (clipboard.getData("text/html")) return false;

            const text = clipboard.getData("text/plain");
            if (!text || !/[\r\n]/.test(text)) return false;

            const html = plaintextToEditorHtml(extension.options.transform(text));
            if (!html) return false;

            extension.editor.commands.insertContent(html);
            return true;
          },
        },
      }),
    ];
  },
});

/**
 * Monta a lista de extensões do editor.
 *
 * @param {{ placeholder?: string, unwrapPastedLineBreaks?: boolean }} [options]
 */
export function buildEditorExtensions(options = {}) {
  return [
    // Decoração de editor: NÃO entra no getHTML(), então não afeta o contrato.
    Placeholder.configure({
      placeholder: options.placeholder ?? "Digite ou cole o conteúdo…",
    }),

    PdfPasteUnwrap.configure({
      enabled: options.unwrapPastedLineBreaks ?? false,
      ...(options.pasteTransform ? { transform: options.pasteTransform } : {}),
    }),

    StarterKit.configure({
      // `code` emite <code>, que a whitelist desembrulha: formatação que o
      // usuário perderia sem aviso. Fora.
      code: false,

      // `codeBlock` emite <pre><code>. O <code> cai e o <pre> fica, então o bloco
      // sobrevive — mas com a fonte herdada pelo CSS de .text-body--html, o que
      // descaracteriza um bloco de código. Desligado até a decisão da seção 9 do
      // plano do editor.
      codeBlock: false,

      // Desligado aqui para entrar como LitteraeLink, abaixo — a limpeza dos
      // atributos padrão exige sobrescrever `addOptions`, o que `configure` não faz.
      link: false,
    }),

    LitteraeLink,
    LitteraeAnchor,
    LitteraeBlockAnchor.configure({ types: ANCHORABLE_TYPES }),
    LitteraeImage,
    LitteraeFigure,

    // Marca de suporte para família e corpo — é ela que produz o <span>.
    LitteraeTextStyle,

    LitteraeTextAlign.configure({ types: ALIGNABLE_TYPES }),
    LitteraeIndent.configure({ types: INDENTABLE_TYPES }),
    LitteraeNoteRule,
    LitteraeRunningHead,
    LitteraeFontFamily,
    LitteraeFontSize,

    Superscript,
    Subscript,

    // Redimensionar coluna arrastando a divisa. A largura é gravada no atributo
    // `colwidth` da célula, que a whitelist aceita (inteiro validado, como
    // colspan) — o <colgroup> com `style` que o Tiptap mostra é DERIVADO dele e
    // reconstruído a cada carga, então não precisa sobreviver.
    Table.configure({ resizable: true }),
    TableRow,
    TableHeader,
    TableCell,
  ];
}
